import { Link } from "react-router-dom";
import { Target, Eye, Shield, TrendingUp, Award, Users } from "lucide-react";
import { useContent } from "../contexts/ContentContext";

const VALUE_ICONS = { Trust: Shield, Integrity: Award, Precision: Target, Growth: TrendingUp };

export default function AboutPage() {
  const { getPage } = useContent();
  const a = getPage("about", {});

  const values = a.values || [
    { title: "Trust", desc: "Built on transparency and unwavering commitment to our clients' best interests." },
    { title: "Integrity", desc: "Ethical practices and honest counsel guide every engagement." },
    { title: "Precision", desc: "Meticulous analysis and disciplined execution in every transaction." },
    { title: "Growth", desc: "Creating sustainable, long-term value for enterprises and their stakeholders." },
  ];

  return (
    <div data-testid="about-page">
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{a.hero_label || "About Capstone"}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight leading-tight max-w-3xl">{a.hero_headline || "Building bridges between ambition and capital."}</h1>
          <p className="text-white/60 font-sans text-base md:text-lg mt-6 max-w-2xl leading-relaxed">{a.hero_subtext || ""}</p>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{a.story_label || "Our Story"}</p>
              <h2 className="font-serif text-4xl text-navy-900 font-light tracking-tight mb-6">{a.story_headline || "From global banks to entrepreneurial advisory."}</h2>
              <div className="space-y-4 text-gray-600 font-sans leading-relaxed">
                {(a.story_paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1764410481612-7544525b2991?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBtZWV0aW5nJTIwbHV4dXJ5JTIwb2ZmaWNlfGVufDB8fHx8MTc3NTM2NTcxMXww&ixlib=rb-4.1.0&q=85" alt="Corporate meeting" className="w-full aspect-[4/3] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-gray-200">
            <div className="p-12 md:p-16 bg-white border-b md:border-b-0 md:border-r border-gray-200">
              <Eye className="w-8 h-8 text-gold-500 mb-6" strokeWidth={1.5} />
              <h3 className="font-serif text-3xl text-navy-900 mb-4">{a.vision_title || "Our Vision"}</h3>
              <p className="text-gray-600 font-sans leading-relaxed">{a.vision_text || ""}</p>
            </div>
            <div className="p-12 md:p-16 bg-white">
              <Target className="w-8 h-8 text-gold-500 mb-6" strokeWidth={1.5} />
              <h3 className="font-serif text-3xl text-navy-900 mb-4">{a.mission_title || "Our Mission"}</h3>
              <p className="text-gray-600 font-sans leading-relaxed">{a.mission_text || ""}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{a.values_label || "Our Foundation"}</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-navy-900 font-light tracking-tight">{a.values_headline || "Core Values"}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-gray-200">
            {values.map((v, i) => {
              const Icon = VALUE_ICONS[v.title] || Shield;
              return (
                <div key={v.title} className={`p-8 md:p-10 ${i < 3 ? 'border-b sm:border-b lg:border-b-0 sm:border-r lg:border-r border-gray-200' : ''} ${i === 2 ? 'sm:border-r-0 lg:border-r' : ''}`}>
                  <Icon className="w-7 h-7 text-gold-500 mb-5" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl text-navy-900 mb-2">{v.title}</h3>
                  <p className="text-gray-500 font-sans text-sm leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-navy-900 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <Users className="w-10 h-10 text-gold-500 mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-serif text-4xl sm:text-5xl text-white font-light tracking-tight mb-6">{a.global_headline || "Global Perspective. Indian Roots."}</h2>
          <p className="text-white/60 font-sans text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-10">{a.global_text || ""}</p>
          <Link to="/contact" data-testid="about-cta-button" className="inline-flex items-center gap-2 bg-gold-500 text-navy-900 px-8 py-4 rounded-sm font-sans text-sm font-medium hover:bg-gold-400 transition-colors duration-300">Start a Conversation</Link>
        </div>
      </section>
    </div>
  );
}
